import { FEEDBACK_SOURCES } from '../Misc/ComprehendFeedback';
import { isAgenticLearnUser } from '../../utils/agenticLearn';
import { persistInstructionUpdates } from '../InstructionAssistant/persistInstructionUpdates';
import {
  TEXT_TYPES,
  describeInstruction,
  editInstruction,
  flexAnswerFromEntries,
  flexAnswerFromStructure,
  flexFieldEntries,
  flexFields,
  flexValueAtPath,
  instructionText,
  isFlexPlusPlus,
  planChoiceWrite,
  planFlexWrite,
  parseFlexInstruction,
  restoreSubsections,
  viewMoreNames,
} from './sectionValues';
import { sameText } from './textDiff';

// The visit view's own note and template functions, offered to the chat as
// tools (COM-536). Nothing here talks to the EMR directly: every read and write
// goes through what the visit view already uses - createEMRSession and its
// answerQuestionInEMR, the section scan, comprehendTemplate, the template
// saves, the Instruction Assistant hook - reached through `getHost()`, so the
// injector stays untouched and the tools see the view's current state.
//
// Host (built by ConversationDetailsCX):
//   apiClient, conversationId, configId, transcript, visitNotes, templateReply
//   loadVisitTemplate()                -> discover + attach the open note's template
//   createEMRSession()                 -> pinned EMR session (tab + patient fingerprint)
//   scanNote(session)                  -> scraped sections, each with currentAnswer
//   checkPatientMatch(session)         -> null, or {visitPatientName, emrPatientName}
//   setPatient(session)                -> adopt the EMR's patient when the visit has none
//   comprehendTemplate(keys, statusCb, options), dispatchComprehendStatus
//   pushEmrUndo(session, questions)    -> feeds the panel's existing Revert
//   getStatus()                        -> the panel's status line
//   appendVisitNotes(text)             -> Additional Visit Information (saved)
//   setTemplateReply(key, replyType)   -> the section Skip/append/overwrite save
//   applyLocalInstructions(map)        -> show saved instructions in the panel
//   getInstructionAssistantSectionMetadata(), instructionAssistantUpdate

const EMR_TIMEOUT_MS = 45000;
const TEXT_LIMIT = 1500;
const INSTRUCTION_LIMIT = 3000;
const EXAMPLE_LIMIT = 400;
// get_change: one earlier value, kept well under the server's per-result cap
const CHANGE_VALUE_LIMIT = 8000;
// Keep get_template under the server's per-result cap, so nothing is cut silently
const TEMPLATE_BUDGET = 24000;
const REPLY_TYPES = ['overwrite', 'append', 'no_reply'];
const REPLY_TYPE_MEANING = {
  overwrite: 'replaces what is in the field',
  append: 'adds after what is already in the field',
  no_reply: 'Skip: Comprehend leaves this section alone',
};

const clip = (value, limit) => {
  const text = typeof value === 'string' ? value : value == null ? '' : JSON.stringify(value);
  return text.length > limit ? `${text.slice(0, limit)}…` : text;
};

const normalize = (value) =>
  String(value ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

function withTimeout(promise, label) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(`${label}: no answer from the EMR page`)), EMR_TIMEOUT_MS);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

const destinationOf = (session) => {
  const info = session?.initialPageInfo || {};
  let host = '';
  try {
    host = info.URL ? new URL(info.URL).hostname : '';
  } catch (err) {
    host = '';
  }
  return [host, info['Note Type']].filter(Boolean).join(' · ');
};

async function openNote(host) {
  let session;
  try {
    session = await host.createEMRSession();
  } catch (err) {
    return { error: 'Open the patient note in your EMR first.' };
  }
  const sections = await withTimeout(host.scanNote(session), 'Reading the note');
  if (session.isDisposed?.()) return { error: 'The EMR page changed while reading it.' };
  if (!sections || sections.length === 0) {
    return { error: 'No note sections found. Open the patient note in your EMR.' };
  }
  return { session, sections };
}

// Only a template the clinician owns or has Full access to can be changed
const EDITABLE_ACCESS = new Set(['Full', 'Owner']);

async function loadEditableTemplate(host) {
  const loaded = await readLoadedTemplate(host);
  if (loaded.error) return loaded;
  const access = loaded.details.access_level || 'Full';
  if (!EDITABLE_ACCESS.has(access)) {
    return { error: `${loaded.details.name || 'This template'} is shared with you as ${access}, so it can't be changed from here.` };
  }
  return loaded;
}

// Each Flex section's View More subsection names, from a scan of the open note ({} when there's no note open)
async function viewMoreByKey(host) {
  const opened = await openNote(host).catch(() => ({ error: true }));
  if (opened.error) return {};
  return Object.fromEntries(
    opened.sections.filter((section) => section.Type === 'Flex').map((section) => [section.Key, viewMoreNames(section.Structure)]),
  );
}

const MAX_EXAMPLES_SHOWN = 10;

// undo_change's `only`: section keys or titles, subsection / group names, or field names
function matcherFor(only) {
  if (!Array.isArray(only) || only.length === 0) return null;
  const norm = (name) => String(name).trim().toLowerCase();
  const wanted = new Set(only.map(norm));
  // "Plan" matches "Plan", "1 SOAP › Plan" and a Flex field named "Plan - Notes"
  const forms = (name) => {
    const full = norm(name);
    const last = full.split('›').pop().trim();
    return [full, last, last.split(' - ')[0].trim()];
  };
  return (...names) => names.some((name) => name != null && forms(name).some((form) => wanted.has(form)));
}

async function readLoadedTemplate(host) {
  const configId = host.configId;
  if (!configId || configId === -1) {
    return { error: 'No template is loaded for this visit. Call load_template, then retry this tool.' };
  }
  const details = await host.apiClient.getConfigDetails(configId);
  if (!details || details.error) {
    return { error: typeof details?.error === 'string' ? details.error : 'Could not load the template.' };
  }
  return { details, configId };
}

const sectionTitle = (section) => clip(section.Question || section.Key, 120);

const sectionText = (section) => section.currentAnswer ?? section.ExistingNote ?? '';
const sectionValue = (section) => section.Type === 'Flex'
  ? flexAnswerFromStructure(section.Structure, section.liveCurrentAnswer)
  : sectionText(section);
const flexPathKey = (path) => JSON.stringify((path || []).map((part) => String(part)));
const flexPathLabel = (path) => (path || []).map((part) => typeof part === 'number' ? part + 1 : part).join(' › ');
const fieldHasValue = (value) => Array.isArray(value) ? value.length > 0 : value != null && String(value).trim() !== '';
const shownFieldValue = (value) => {
  if (Array.isArray(value)) return value.map(shownFieldValue);
  return typeof value === 'string' ? clip(value, 400) : value;
};

// Flex fields without their options: just what is filled in, and how many fields there are
function filledFlexFields(fields) {
  return Object.fromEntries(
    Object.entries(fields).map(([group, groupFields]) => {
      if ('rows' in groupFields) return [group, groupFields];
      const filled = Object.entries(groupFields).filter(([, field]) => field.value);
      return [group, { fieldCount: Object.keys(groupFields).length, filled: Object.fromEntries(filled.map(([name, field]) => [name, clip(field.value, 400)])) }];
    }),
  );
}

function describedFlexFields(structure, currentAnswer, detailed) {
  const entries = flexFieldEntries(structure, currentAnswer);
  const simpleLegacy = !isFlexPlusPlus(structure) && entries.length > 0 && entries.every(
    ({ path }) => path.length === 2 && path.every((part) => typeof part === 'string'),
  );
  if (simpleLegacy) {
    const fields = flexFields(structure);
    return detailed ? fields : filledFlexFields(fields);
  }
  const shown = entries.map(({ path, value, choices, multiple, valueType }) => ({
    path,
    value: shownFieldValue(value),
    ...(choices && { choices: choices.slice(0, 60) }),
    ...(multiple && { multiple: true }),
    ...(valueType && { valueType }),
  }));
  return {
    fieldCount: entries.length,
    writeWith: 'field_updates',
    ...(detailed
      ? { entries: shown }
      : { filled: shown.filter(({ value }) => fieldHasValue(value)).map(({ path, value }) => ({ path, value })) }),
  };
}

function describeSection(section, host, detailed = false) {
  const base = {
    key: section.Key,
    title: sectionTitle(section),
    type: section.Type,
    replyType: host.templateReply?.[section.Key] || 'overwrite',
  };
  if (section.Type === 'Flex') {
    return {
      ...base,
      representation: isFlexPlusPlus(section.Structure) ? 'flex++' : 'flex',
      fields: describedFlexFields(section.Structure, section.liveCurrentAnswer, detailed),
    };
  }
  if (section.Type === 'Multiple Choice' || section.Type === 'Choose All') {
    return { ...base, currentValue: clip(sectionText(section), TEXT_LIMIT), choices: (section.Choices || []).slice(0, 60) };
  }
  return { ...base, currentText: clip(sectionText(section), TEXT_LIMIT) };
}

// What a section held, exactly enough to put it back (undo_change)
const noteSnapshot = (section, flexChanges = null) => {
  let answer = section.Type === 'Flex'
    ? flexAnswerFromStructure(section.Structure, section.liveCurrentAnswer)
    : String(sectionText(section));
  if (section.Type === 'Flex' && flexChanges?.length) {
    const currentEntries = flexFieldEntries(section.Structure, section.liveCurrentAnswer);
    const current = new Map(currentEntries.map((field) => [flexPathKey(field.path), field]));
    answer = flexAnswerFromEntries(
      flexChanges
        .map(({ path, group, field }) => current.get(flexPathKey(path || [group, field])))
        .filter(Boolean),
    );
  }
  return {
    key: section.Key,
    question: section.Question,
    title: sectionTitle(section),
    type: section.Type,
    answer,
    ...(section.Type === 'Flex' && flexChanges?.length && {
      paths: flexChanges.map(({ path, group, field }) => path || [group, field]),
    }),
  };
};

const snapshotFlexEntries = (snapshot, currentSection) => {
  const wantedPaths = snapshot.paths?.length
    ? new Set(snapshot.paths.map(flexPathKey))
    : null;
  return flexFieldEntries(currentSection.Structure, currentSection.liveCurrentAnswer)
    .map(({ path }) => ({ path, value: flexValueAtPath(snapshot.answer, path) }))
    .filter(({ path, value }) => value !== undefined && (!wantedPaths || wantedPaths.has(flexPathKey(path))));
};

const snapshotAnswerForWrite = (snapshot, currentSection) => {
  if (snapshot.type !== 'Flex') return snapshot.answer;
  return flexAnswerFromEntries(
    snapshotFlexEntries(snapshot, currentSection),
    flexFieldEntries(currentSection.Structure, currentSection.liveCurrentAnswer),
  );
};

const sameFieldValue = (left, right) =>
  typeof left === 'string' && typeof right === 'string'
    ? sameText(left, right)
    : normalize(JSON.stringify(left)) === normalize(JSON.stringify(right));

// One line of "what changed", kept with the chat so later turns remember it
const changeLine = (label, before, after) => `${label}: "${clip(before, 300)}" → "${clip(after, 300)}"`;

const flexChangeLines = (changes) =>
  changes.map(({ path, group, field, before, after }) => ({
    label: flexPathLabel(path || [group, field]),
    before,
    after,
  }));

// Facts about one patient don't belong in instructions that write every patient's note
const PATIENT_FACT = /\bthis patient\b|\bpatient(?:'s)? (?:name|age|sex|gender|dob) is\b|\bpatient is (?:a )?(?:male|female|man|woman|boy|girl)\b/i;

// Examples are text, but older templates can hold structured (Flex) ones
const exampleText = (example) => (typeof example === 'string' ? example : JSON.stringify(example));

const escapeRegExp = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const FULL_DATE = /\b\d{1,2}[/-]\d{1,2}[/-]\d{2,4}\b|\b\d{4}-\d{2}-\d{2}\b/;

// What in a new example (or instruction) is plainly about this visit: the
// patient's name, or (examples) a full date. Examples teach every future note,
// and Comprehend copies what it sees in them, so these must be generic first.
function visitDetailIn(text, host, { dates = true } = {}) {
  const names = (host.getPatientNames?.() || []).filter((name) => typeof name === 'string' && name.trim().length >= 3);
  const name = names.find((candidate) => new RegExp(`\\b${escapeRegExp(candidate.trim())}\\b`, 'i').test(text));
  if (name) return `the patient's name ("${name.trim()}")`;
  const date = dates && String(text).match(FULL_DATE);
  if (date) return `a date ("${date[0]}")`;
  return null;
}

export function createVisitNoteTools(getHost, { canWrite = true, canEditTemplate = true } = {}) {
  const getNoteSections = {
    name: 'get_note_sections',
    description:
      "Read the note open in the clinician's EMR as it is right now: each section's key, title, type and reply type, plus its current text (text sections) or current value and options (choice sections). Flex sections list their filled-in fields; pass section_keys to get every field of those sections with its value and options (needed before writing one). Also says whether the EMR shows this visit's patient.",
    parameters: {
      type: 'object',
      properties: {
        section_keys: { type: 'array', items: { type: 'string' }, description: 'Sections to show in full detail (optional)' },
      },
      required: [],
    },
    handler: async ({ section_keys: detailKeys }) => {
      const host = getHost();
      const opened = await openNote(host);
      if (opened.error) return { ok: false, error: opened.error };
      const mismatch = await host.checkPatientMatch(opened.session);
      return {
        ok: true,
        destination: destinationOf(opened.session),
        patientMismatch: mismatch || null,
        sections: opened.sections
          .filter((section) => !Array.isArray(detailKeys) || detailKeys.length === 0 || detailKeys.includes(section.Key))
          .map((section) => describeSection(section, host, Array.isArray(detailKeys) && detailKeys.includes(section.Key))),
      };
    },
  };

  const loadVisitTemplate = {
    name: 'load_template',
    description:
      'Load and attach the template that matches the note currently open in the EMR. Call this before get_template or any template-editing tool when the visit context says no template is loaded, or when one of those tools tells you to call load_template. This does not run Comprehend or rewrite the note.',
    parameters: { type: 'object', properties: {}, required: [] },
    handler: async () => {
      const host = getHost();
      if (host.configId && host.configId !== -1) {
        const loaded = await readLoadedTemplate(host);
        if (loaded.error) return { ok: false, error: loaded.error };
        return {
          ok: true,
          alreadyLoaded: true,
          configId: loaded.configId,
          name: loaded.details.name,
          note: 'The template was already loaded.',
        };
      }
      if (!host.loadVisitTemplate) {
        return { ok: false, error: 'Template loading is not available in this visit.' };
      }
      let loaded;
      try {
        loaded = await host.loadVisitTemplate();
      } catch (err) {
        return { ok: false, error: err?.message || 'Could not load the template for the open note.' };
      }
      if (!loaded?.configId || loaded.configId === -1) {
        return {
          ok: false,
          error: 'No template matched the note open in the EMR. Open the note type or choose a template, then retry load_template.',
        };
      }
      // Keep the just-loaded id available if React has not committed its state
      // update before the model makes the next tool call.
      host.configId = loaded.configId;
      return {
        ok: true,
        loaded: true,
        configId: loaded.configId,
        name: loaded.configDetails?.name,
        note: 'Template loaded. Retry the original template tool now.',
      };
    },
  };

  const getTemplate = {
    name: 'get_template',
    description:
      "Read the clinician's loaded template for the note that is open: for each of its sections, the EMR title, the instruction Comprehend follows (Flex sections: the top-level instruction and each subsection's instruction and reply type), the reply type, View More subsections that have no instruction yet, and its examples (all of them, numbered, when you pass section_keys). If it says no template is loaded, call load_template and retry. It also says whether the clinician can change the template (accessLevel Full) or only read it. Templates often also hold sections for other note types; those are listed by key, and section_keys reads any of them.",
    parameters: {
      type: 'object',
      properties: {
        section_keys: { type: 'array', items: { type: 'string' }, description: 'Read these sections instead of the open note’s (optional)' },
      },
      required: [],
    },
    handler: async ({ section_keys: only }) => {
      const host = getHost();
      const loaded = await readLoadedTemplate(host);
      if (loaded.error) return { ok: false, error: loaded.error };
      const { details } = loaded;
      const questions = details.questions || {};
      const templateKeys = Object.keys(questions);

      // Default to the sections on the open note, with their EMR titles: a
      // template can hold hundreds of sections for other note types
      let titles = {};
      let keys;
      if (Array.isArray(only) && only.length) {
        keys = only.filter((key) => key in questions);
      } else {
        const opened = await openNote(host);
        if (opened.error) return { ok: false, error: `${opened.error} (or pass section_keys)` };
        titles = Object.fromEntries(opened.sections.map((section) => [section.Key, sectionTitle(section)]));
        keys = opened.sections.map((section) => section.Key).filter((key) => key in questions);
      }

      const viewMore = await viewMoreByKey(host);
      const detailed = Array.isArray(only) && only.length > 0;
      const sections = [];
      const notShown = [];
      let size = 0;
      keys.forEach((key) => {
        const examples = Array.isArray(details.samples?.[key]) ? details.samples[key] : [];
        const instruction = describeInstruction(questions[key], INSTRUCTION_LIMIT);
        const saved = new Set((instruction.subsections || []).map((sub) => sub.name));
        const unsaved = (viewMore[key] || []).filter((name) => !saved.has(name));
        const entry = {
          key,
          ...(titles[key] && titles[key] !== key && { title: titles[key] }),
          replyType: details.replyType?.[key] || 'overwrite',
          ...instruction,
          // View More subsections with no instruction of their own yet; update_instructions can give them one
          ...(unsaved.length && { viewMoreWithoutInstruction: unsaved }),
          examples: detailed
            ? {
                count: examples.length,
                items: examples.slice(0, MAX_EXAMPLES_SHOWN).map((example, index) => ({
                  index,
                  text: clip(exampleText(example), 600),
                  ...(typeof example !== 'string' && { structured: true }),
                })),
              }
            : { count: examples.length, ...(examples.length && { first: clip(exampleText(examples[0]), EXAMPLE_LIMIT) }) },
        };
        size += JSON.stringify(entry).length;
        if (size > TEMPLATE_BUDGET) notShown.push(key);
        else sections.push(entry);
      });
      const otherKeys = templateKeys.filter((key) => !keys.includes(key));
      return {
        ok: true,
        name: details.name,
        accessLevel: details.access_level || 'Owner',
        sections,
        ...(notShown.length && { notShown, note: 'Too long to show at once; read these with section_keys.' }),
        ...(otherKeys.length && !only?.length && { otherSectionCount: otherKeys.length, otherSectionKeys: otherKeys.slice(0, 150) }),
        ...(keys.length < (only || []).length && { unknownKeys: only.filter((key) => !(key in questions)) }),
      };
    },
  };

  const comprehendNote = {
    name: 'comprehend_note',
    permissionKey: 'comprehend_note@1',
    permissionLabel: 'Re-run Comprehend on the note',
    description:
      'Re-run Comprehend from the transcript, Additional Visit Information and the template: the whole note (no section_keys) or only the given sections. It rewrites those sections in the EMR, the same as the Comprehend and section refresh buttons. Asks the clinician first.',
    parameters: {
      type: 'object',
      properties: {
        section_keys: {
          type: 'array',
          items: { type: 'string' },
          description: 'Section keys from get_note_sections. Leave out for the whole note.',
        },
      },
      required: [],
    },
    handler: async ({ section_keys: sectionKeys }, ctx) => {
      const host = getHost();
      const opened = await openNote(host);
      if (opened.error) return { ok: false, error: opened.error };
      const byKey = new Map(opened.sections.map((section) => [section.Key, section]));
      const wholeNote = !Array.isArray(sectionKeys) || sectionKeys.length === 0;
      const keys = wholeNote ? [...byKey.keys()] : sectionKeys;
      const unknown = keys.filter((key) => !byKey.has(key));
      if (unknown.length) return { ok: false, error: `Unknown section keys: ${unknown.join(', ')}` };
      const titles = keys.map((key) => sectionTitle(byKey.get(key)));

      const allowed = await ctx.requestPermission({
        title: wholeNote ? 'Re-run Comprehend on the whole note' : `Re-run Comprehend on ${titles.join(', ')}`,
        destination: destinationOf(opened.session),
        description:
          'Rewrites these sections from the transcript and your template. You still save and sign.',
        sections: titles,
      });
      if (!allowed) return { ok: false, denied: true, error: 'The clinician did not allow this.' };

      await host.comprehendTemplate(wholeNote ? undefined : keys, host.dispatchComprehendStatus, {
        source: wholeNote ? FEEDBACK_SOURCES.comprehend : FEEDBACK_SOURCES.sectionRefresh,
      });

      // What actually changed, read back from the EMR - not what we hoped for
      const after = await openNote(host);
      if (after.error) return { ok: false, error: `Comprehend ran, but the note could not be read back: ${after.error}` };
      const afterByKey = new Map(after.sections.map((section) => [section.Key, section]));
      const changed = [];
      const unchanged = [];
      keys.forEach((key) => {
        const before = byKey.get(key);
        const now = afterByKey.get(key);
        const entry = { key, title: sectionTitle(before) };
        if (now && normalize(JSON.stringify(sectionValue(now))) !== normalize(JSON.stringify(sectionValue(before)))) {
          changed.push({ ...entry, text: clip(sectionValue(now), TEXT_LIMIT) });
        } else {
          unchanged.push(entry);
        }
      });
      return {
        ok: changed.length > 0,
        changed,
        unchanged,
        // getHost() again: the host captured above predates the Comprehend run
        panelStatus: getHost().getStatus?.() || '',
        ...(changed.length === 0 && { error: 'No section changed. Comprehend may have stopped; see the panel status.' }),
        action: {
          status: changed.length ? 'done' : 'no_change',
          summary: changed.length
            ? `Re-comprehended ${changed.map((c) => c.title).join(', ')}`
            : 'Re-ran Comprehend; nothing changed',
          ...(changed.length && {
            detail: changed
              .map((c) => changeLine(c.title, JSON.stringify(sectionValue(byKey.get(c.key))), JSON.stringify(sectionValue(afterByKey.get(c.key)))))
              .join('\n'),
            undo: { kind: 'note', sections: changed.map((c) => noteSnapshot(byKey.get(c.key))) },
          }),
        },
      };
    },
  };

  const writeSection = {
    name: 'write_section',
    permissionKey: 'write_section@2',
    permissionLabel: 'Write a note section',
    description:
      "Write into one section of the note in the EMR, for one-off changes to this visit's note. Text/Integer/String sections: give text (mode replace or append). Multiple Choice: give choice. Choose All: give choices. Two-level legacy Flex can use fields as { group: { field: value } }. Flat, list, mixed, and Flex++ sections use field_updates with the exact paths from get_note_sections. Send only fields that change. Asks the clinician first, showing before and after.",
    parameters: {
      type: 'object',
      properties: {
        section_key: { type: 'string', description: 'Section key from get_note_sections' },
        text: { type: 'string', description: 'Text sections: the text to write' },
        mode: { type: 'string', enum: ['replace', 'append'], description: 'Text sections: replace (default) or append' },
        choice: { type: 'string', description: 'Multiple Choice: the option to select' },
        choices: { type: 'array', items: { type: 'string' }, description: 'Choose All: the options to select' },
        fields: {
          type: 'object',
          description: 'Two-level legacy Flex only: { group: { field: value } }, only the fields that change',
          additionalProperties: { type: 'object', additionalProperties: { type: 'string' } },
        },
        field_updates: {
          type: 'array',
          description: 'Flat/list/mixed/Flex++ fields: exact path and new value from get_note_sections',
          items: {
            type: 'object',
            properties: {
              path: { type: 'array', items: {}, description: 'Exact string/row-index path from get_note_sections' },
              value: { description: 'New string, number, or list of choices' },
            },
            required: ['path', 'value'],
          },
        },
      },
      required: ['section_key'],
    },
    handler: async ({ section_key: key, text, mode = 'replace', choice, choices, fields, field_updates: fieldUpdates }, ctx) => {
      const host = getHost();
      const opened = await openNote(host);
      if (opened.error) return { ok: false, error: opened.error };
      const { session } = opened;
      const section = opened.sections.find((candidate) => candidate.Key === key);
      if (!section) return { ok: false, error: `Unknown section key ${key}` };

      // What to write, checked against what the section can hold
      let answer;
      let preview;
      let flexChanges = null;
      const before = sectionText(section);
      if (section.Type === 'Flex') {
        const plan = planFlexWrite(section.Structure, fields, fieldUpdates, section.liveCurrentAnswer);
        if (plan.error) return { ok: false, error: plan.error };
        answer = plan.answer;
        flexChanges = plan.changes;
        preview = { edits: flexChangeLines(plan.changes) };
      } else if (section.Type === 'Multiple Choice' || section.Type === 'Choose All') {
        const plan = planChoiceWrite(section, { choice, choices });
        if (plan.error) return { ok: false, error: plan.error };
        if (sameText(plan.after, before)) return { ok: false, noChange: true, error: 'No change: the section already says that.' };
        answer = plan.answer;
        preview = { before: String(before), after: plan.after };
      } else if (TEXT_TYPES.has(section.Type) || !section.Type) {
        if (typeof text !== 'string' || !text.trim()) return { ok: false, error: 'text is empty' };
        answer = mode === 'append' && String(before).trim() ? `${before}\n${text}` : text;
        if (sameText(answer, before)) return { ok: false, noChange: true, error: 'No change: the section already says that.' };
        preview = { before: String(before), after: answer };
      } else {
        return { ok: false, error: `${sectionTitle(section)} is a ${section.Type} section; use comprehend_note for it.` };
      }

      const mismatch = await host.checkPatientMatch(session);
      if (mismatch) {
        return {
          ok: false,
          error: `The EMR shows ${mismatch.emrPatientName}, but this visit is ${mismatch.visitPatientName}. Nothing was written.`,
        };
      }
      const allowed = await ctx.requestPermission({
        title: `Write ${sectionTitle(section)} in your EMR`,
        destination: destinationOf(session),
        description: 'Fills the field only. You still save and sign.',
        ...preview,
      });
      if (!allowed) return { ok: false, denied: true, error: 'The clinician did not allow this.' };
      if (session.isDisposed?.()) {
        return { ok: false, error: 'The EMR page changed (another note or patient). Nothing was written.' };
      }

      await host.setPatient?.(session);
      const undoSnapshot = noteSnapshot(section, flexChanges);
      host.pushEmrUndo(session, [
        {
          Key: section.Key,
          Type: section.Type,
          ExistingNote: section.ExistingNote,
          Structure: section.Structure,
          Answer: snapshotAnswerForWrite(undoSnapshot, section),
        },
      ]);
      try {
        await withTimeout(session.answerQuestionInEMR(section.Question, answer, 'overwrite', key), 'Writing');
      } catch (err) {
        return {
          ok: false,
          outcome: 'unknown',
          error: `${err.message}. The field may or may not have changed; check it before trying again.`,
        };
      }
      if (session.isDisposed?.()) {
        return { ok: false, outcome: 'unknown', error: 'The EMR page changed during the write; check the field.' };
      }
      if (typeof answer === 'string') {
        // Keep the visit's draft in step with the EMR, as a manual section edit does
        host.apiClient.editConversation(host.conversationId, key, answer)?.catch?.(() => {});
      }

      const readBack = await withTimeout(host.scanNote(session), 'Reading back').catch(() => null);
      const observed = readBack?.find((candidate) => candidate.Key === key);
      let verified = false;
      if (observed && flexChanges) {
        const now = new Map(flexFieldEntries(observed.Structure, observed.liveCurrentAnswer).map((field) => [flexPathKey(field.path), field.value]));
        verified = flexChanges.every(({ path, group, field, after }) =>
          normalize(JSON.stringify(now.get(flexPathKey(path || [group, field])))).includes(normalize(JSON.stringify(after))),
        );
      } else if (observed) {
        const expected = Array.isArray(answer) ? answer : [answer];
        verified = expected.every((value) => normalize(sectionText(observed)).includes(normalize(value)));
      }
      return {
        ok: true,
        verified,
        section: sectionTitle(section),
        ...(!verified && { observed: observed ? describeSection(observed, host, Boolean(flexChanges)) : null, note: 'Read-back did not match what was written.' }),
        action: {
          status: verified ? 'done' : 'unverified',
          summary: `${mode === 'append' && !flexChanges ? 'Added to' : 'Updated'} ${sectionTitle(section)}${verified ? '' : ' (read-back did not match)'}`,
          detail: flexChanges
            ? flexChanges.map(({ path, group, field, before: was, after: now }) => changeLine(flexPathLabel(path || [group, field]), was, now)).join('\n')
            : changeLine(sectionTitle(section), before, preview.after),
          undo: { kind: 'note', sections: [undoSnapshot] },
        },
      };
    },
  };

  const addVisitInformation = {
    name: 'add_visit_information',
    permissionKey: 'add_visit_information@1',
    permissionLabel: 'Add to Additional Visit Information',
    description:
      "Add text to this visit's Additional Visit Information, which Comprehend reads alongside the transcript. Use it for facts the transcript is missing, then re-run the affected sections with comprehend_note. Asks the clinician first.",
    parameters: {
      type: 'object',
      properties: { text: { type: 'string', description: 'What to add, as the clinician stated it' } },
      required: ['text'],
    },
    handler: async ({ text }, ctx) => {
      if (typeof text !== 'string' || !text.trim()) return { ok: false, error: 'text is empty' };
      const host = getHost();
      const before = host.visitNotes || '';
      const allowed = await ctx.requestPermission({
        title: 'Add to Additional Visit Information',
        description: 'Comprehend reads this with the transcript the next time a section is written.',
        before,
        after: before.trim() ? `${before.replace(/\s+$/, '')}\n${text.trim()}` : text.trim(),
      });
      if (!allowed) return { ok: false, denied: true, error: 'The clinician did not allow this.' };
      host.appendVisitNotes(text);
      return {
        ok: true,
        action: {
          status: 'done',
          summary: 'Added to Additional Visit Information',
          detail: `Added: "${clip(text, 400)}"`,
          undo: { kind: 'visit_notes', visitNotes: before },
        },
      };
    },
  };

  const updateInstructions = {
    name: 'update_instructions',
    permissionKey: 'update_instructions@1',
    permissionLabel: 'Edit template instructions',
    description:
      "Directly rewrite template instructions when the clinician specifies the exact wording or an unambiguous rule and the target section or subsection is known. Each change gives a section_key and the full new instruction text, plus subsection for one subsection of a Flex section, including View More subsections that have no instruction yet (leave it out for a Flex section's top-level instruction). Read the current text with get_template first and change only what the clinician asked. For broad or outcome-based feedback whose wording or affected sections must be inferred, such as 'the note was too wordy', use instruction_assistant instead. Applies to every future note on this template. Asks the clinician first, showing old and new text.",
    parameters: {
      type: 'object',
      properties: {
        changes: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              section_key: { type: 'string' },
              subsection: { type: 'string' },
              instruction: { type: 'string', description: 'The full new instruction text' },
            },
            required: ['section_key', 'instruction'],
          },
        },
      },
      required: ['changes'],
    },
    handler: async ({ changes }, ctx) => {
      if (!Array.isArray(changes) || changes.length === 0) return { ok: false, error: 'No changes given' };
      const host = getHost();
      const loaded = await loadEditableTemplate(host);
      if (loaded.error) return { ok: false, error: loaded.error };
      const questions = { ...(loaded.details.questions || {}) };
      const viewMore = changes.some((change) => change?.subsection) ? await viewMoreByKey(host) : {};
      const edits = [];
      for (const change of changes) {
        const key = change?.section_key;
        if (!(key in questions)) return { ok: false, error: `No section "${key}" in the template` };
        if (typeof change.instruction !== 'string' || !change.instruction.trim()) {
          return { ok: false, error: `Empty instruction for ${key}` };
        }
        const visitDetail = visitDetailIn(change.instruction, host, { dates: false });
        if (PATIENT_FACT.test(change.instruction) || visitDetail) {
          return {
            ok: false,
            error: `The new ${key} instruction mentions this patient${visitDetail ? ` (${visitDetail})` : ''}; instructions apply to every patient.`,
          };
        }
        const edit = editInstruction(questions[key], {
          subsection: change.subsection,
          instruction: change.instruction,
          viewMore: viewMore[key] || [],
        });
        if (edit.error) return { ok: false, error: `${key}: ${edit.error}` };
        if (sameText(edit.before, edit.after)) continue;
        questions[key] = edit.raw;
        edits.push({ key, label: change.subsection ? `${key} › ${change.subsection}` : key, before: edit.before, after: edit.after });
      }
      if (edits.length === 0) return { ok: false, noChange: true, error: 'No change: those instructions already say that.' };
      const allowed = await ctx.requestPermission({
        title: `Edit instructions in ${loaded.details.name || 'your template'}`,
        description: 'Applies to future notes on this template.',
        edits: edits.map(({ label, before, after }) => ({ label, before, after })),
      });
      if (!allowed) return { ok: false, denied: true, error: 'The clinician did not allow this.' };

      const changedKeys = [...new Set(edits.map((edit) => edit.key))];
      const saved = Object.fromEntries(changedKeys.map((key) => [key, questions[key]]));
      await persistInstructionUpdates(host.apiClient, loaded.configId, saved);
      host.applyLocalInstructions(saved);
      return {
        ok: true,
        updated: edits.map((edit) => edit.label),
        action: {
          status: 'done',
          summary: `Updated instructions: ${edits.map((edit) => edit.label).join(', ')}`,
          detail: edits.map(({ label, before, after }) => changeLine(label, before, after)).join('\n'),
          undo: {
            kind: 'instructions',
            instructions: Object.fromEntries(changedKeys.map((key) => [key, loaded.details.questions[key]])),
          },
        },
      };
    },
  };

  const setReplyType = {
    name: 'set_reply_type',
    permissionKey: 'set_reply_type@1',
    permissionLabel: 'Change a section reply type',
    description:
      "Change how Comprehend fills a section: overwrite (replace what is there), append (add after it) or no_reply (Skip: leave the section alone). Pass subsection for one subsection of a Flex section. Saves to the template. Asks the clinician first.",
    parameters: {
      type: 'object',
      properties: {
        section_key: { type: 'string' },
        subsection: { type: 'string' },
        reply_type: { type: 'string', enum: REPLY_TYPES },
      },
      required: ['section_key', 'reply_type'],
    },
    handler: async ({ section_key: key, subsection, reply_type: replyType }, ctx) => {
      if (!REPLY_TYPES.includes(replyType)) return { ok: false, error: `reply_type must be one of ${REPLY_TYPES.join(', ')}` };
      const host = getHost();
      const loaded = await loadEditableTemplate(host);
      if (loaded.error) return { ok: false, error: loaded.error };
      const raw = (loaded.details.questions || {})[key];
      if (raw === undefined) return { ok: false, error: `No section "${key}" in the template` };

      let edit;
      if (subsection) {
        edit = editInstruction(raw, { subsection, replyType, viewMore: (await viewMoreByKey(host))[key] || [] });
        if (edit.error) return { ok: false, error: edit.error };
      } else {
        edit = { before: loaded.details.replyType?.[key] || 'overwrite', after: replyType };
      }
      if (edit.before === edit.after) return { ok: true, note: `Already ${replyType}` };
      const label = subsection ? `${key} › ${subsection}` : key;
      const allowed = await ctx.requestPermission({
        title: `Change how Comprehend fills ${label}`,
        description: `New setting: ${REPLY_TYPE_MEANING[replyType]}. Saves to your template.`,
        edits: [{ label: 'Reply type', before: edit.before, after: edit.after }],
      });
      if (!allowed) return { ok: false, denied: true, error: 'The clinician did not allow this.' };

      if (subsection) {
        await persistInstructionUpdates(host.apiClient, loaded.configId, { [key]: edit.raw });
        host.applyLocalInstructions({ [key]: edit.raw });
      } else {
        host.setTemplateReply(key, replyType);
      }
      return {
        ok: true,
        action: {
          status: 'done',
          summary: `${label} now ${replyType === 'no_reply' ? 'skipped' : replyType}`,
          detail: changeLine(`${label} reply type`, edit.before, edit.after),
          undo: subsection
            ? { kind: 'instructions', instructions: { [key]: raw } }
            : { kind: 'reply_type', key, replyType: edit.before },
        },
      };
    },
  };

  const instructionAssistant = {
    name: 'instruction_assistant',
    permissionKey: 'instruction_assistant@1',
    permissionLabel: 'Change template instructions',
    description:
      "Run the Instruction Assistant for broad or outcome-based feedback when the desired instruction wording or affected sections must be inferred, such as 'the note was too wordy', 'make my notes more concise', or 'focus more on functional progress'. It turns the clinician's feedback, in their words, into edits using the same planning and generation prompts as the Instruction Assistant button. Not for facts about this patient, one-off fixes to this note, or exact instruction text for a known section (use update_instructions for those). Set also_update_note to rewrite the selected changed sections in this note too. Always shows an inline review where the clinician can remove whole changes or individual sections before applying.",
    parameters: {
      type: 'object',
      properties: {
        feedback: { type: 'string', description: "The clinician's preference, in their words" },
        also_update_note: { type: 'boolean', description: 'Also re-run the changed sections in this note' },
      },
      required: ['feedback'],
    },
    handler: async ({ feedback, also_update_note: alsoUpdateNote = false }, ctx) => {
      if (typeof feedback !== 'string' || !feedback.trim()) return { ok: false, error: 'feedback is empty' };
      if (PATIENT_FACT.test(feedback)) {
        return {
          ok: false,
          error:
            "That reads as a fact about this patient. Template instructions shape every patient's note, so use write_section or comprehend_note for this note, or rephrase it as a general rule.",
        };
      }
      let host = getHost();
      const editable = await loadEditableTemplate(host);
      if (editable.error) return { ok: false, error: editable.error };
      let metadata;
      try {
        metadata = await host.getInstructionAssistantSectionMetadata();
      } catch (err) {
        return { ok: false, error: err?.message || 'Could not scan the current note.' };
      }
      const plan = await host.apiClient.planInstructionChanges(metadata.configId, feedback, metadata.sectionMetadata);
      if (!plan || plan.error) {
        return { ok: false, error: typeof plan?.error === 'string' ? plan.error : 'Could not work out the changes.' };
      }
      const changes = (plan.changes || []).filter((change) => change?.directive && change.sections?.length);
      if (changes.length === 0) return { ok: true, changes: [], note: 'Nothing in the template needs to change.' };

      host = getHost();
      const update = host.instructionAssistantUpdate;
      const agentic = isAgenticLearnUser(host.apiClient);
      const withNote = Boolean(alsoUpdateNote && update.applyTemplateAndNote);
      const permission = await ctx.requestPermission({
        title: withNote ? 'Change template instructions and this note' : 'Change template instructions',
        description: agentic
          ? 'Hands these changes to Agentic Learn, which tunes your template in the background.'
          : `Applies to future notes on this template.${withNote ? ' Also rewrites these sections in this note.' : ''}`,
        changes: changes.map((change) => ({
          summary: change.summary || change.directive,
          directive: change.directive,
          sections: change.sections,
        })),
        selectableChanges: true,
        requireReview: true,
      });
      if (!permission) return { ok: false, denied: true, error: 'The clinician did not allow this.' };

      const kept = Array.isArray(permission.selectedChanges)
        ? permission.selectedChanges
        : changes.map(({ summary, directive, sections }) => ({ summary, directive, sections }));
      if (kept.length === 0) return { ok: false, denied: true, error: 'The clinician removed all planned changes.' };
      host = getHost();
      const applyingUpdate = host.instructionAssistantUpdate || update;
      const context = { transcript: feedback, source: 'typed', visitTranscript: host.transcript || '' };
      // The template before and after, so this can be undone like any other edit
      const beforeTemplate = agentic ? null : editable;
      if (withNote) await applyingUpdate.applyTemplateAndNote(kept, context);
      else await applyingUpdate.applyTemplate(kept, context);
      const sections = [...new Set(kept.flatMap((change) => change.sections))];
      let undo;
      let detail = kept.map((change) => change.summary || change.directive).join('; ');
      if (beforeTemplate?.details) {
        // The Instruction Assistant can rewrite a section's examples as well as its instruction
        const afterTemplate = await readLoadedTemplate(host);
        const was = beforeTemplate.details.questions || {};
        const now = afterTemplate.details?.questions || {};
        const changedKeys = Object.keys(now).filter((key) => key in was && !sameText(was[key], now[key]));
        const examplesWere = beforeTemplate.details.samples || {};
        const examplesNow = afterTemplate.details?.samples || {};
        const examplesOf = (samples, key) => (Array.isArray(samples[key]) ? samples[key] : []);
        const changedExampleKeys = Object.keys(examplesNow).filter(
          (key) => !sameText(JSON.stringify(examplesOf(examplesWere, key)), JSON.stringify(examplesOf(examplesNow, key))),
        );
        if (changedKeys.length || changedExampleKeys.length) {
          undo = {
            kind: 'instructions',
            instructions: Object.fromEntries(changedKeys.map((key) => [key, was[key]])),
            ...(changedExampleKeys.length && {
              examples: Object.fromEntries(changedExampleKeys.map((key) => [key, examplesOf(examplesWere, key)])),
            }),
          };
          detail = [
            ...changedKeys.map((key) => changeLine(key, instructionText(was[key]), instructionText(now[key]))),
            ...changedExampleKeys.map((key) =>
              changeLine(
                `${key} examples`,
                examplesOf(examplesWere, key).map(exampleText).join(' | '),
                examplesOf(examplesNow, key).map(exampleText).join(' | '),
              ),
            ),
          ].join('\n');
        }
      }
      return {
        ok: true,
        handedToAgenticLearn: agentic,
        noteUpdated: withNote,
        changes: kept.map((change) => change.summary || change.directive),
        action: {
          status: 'done',
          summary: `${agentic ? 'Sent to Agentic Learn' : 'Updated instructions'}: ${sections.join(', ')}${withNote ? ' (and this note)' : ''}`,
          detail,
          ...(undo && { undo }),
        },
      };
    },
  };

  const editExamples = {
    name: 'edit_examples',
    permissionKey: 'edit_examples@1',
    permissionLabel: 'Edit section examples',
    description:
      "Change a section's examples, the past text Comprehend imitates for that section: add new ones, remove some, or replace some, by the index get_template (with section_keys) shows. Examples steer style and wording for every future note on this template, and Comprehend copies what it sees, so write them generically: visit details (names, dates, sides, body parts, test names, measurements) as bracketed terms like [Body Part], [Side] or [#]/10. Text with the patient's name or a full date is refused. Structured (Flex) examples can be removed but not rewritten. Asks the clinician first, showing each change.",
    parameters: {
      type: 'object',
      properties: {
        section_key: { type: 'string' },
        add: { type: 'array', items: { type: 'string' }, description: 'New examples to add' },
        remove: { type: 'array', items: { type: 'integer' }, description: 'Indexes of examples to remove' },
        replace: {
          type: 'array',
          items: { type: 'object', properties: { index: { type: 'integer' }, text: { type: 'string' } }, required: ['index', 'text'] },
          description: 'Examples to rewrite, by index',
        },
      },
      required: ['section_key'],
    },
    handler: async ({ section_key: key, add = [], remove = [], replace = [] }, ctx) => {
      const host = getHost();
      const loaded = await loadEditableTemplate(host);
      if (loaded.error) return { ok: false, error: loaded.error };
      if (!((loaded.details.questions || {})[key] !== undefined)) return { ok: false, error: `No section "${key}" in the template` };
      const before = Array.isArray(loaded.details.samples?.[key]) ? loaded.details.samples[key] : [];
      const bad = [...remove, ...replace.map((r) => r?.index)].filter((index) => !Number.isInteger(index) || index < 0 || index >= before.length);
      if (bad.length) return { ok: false, error: `No example at index ${bad.join(', ')}; this section has ${before.length}.` };

      const structured = replace.filter((r) => typeof before[r.index] !== 'string').map((r) => r.index);
      if (structured.length) {
        return { ok: false, error: `Example ${structured.map((i) => i + 1).join(', ')} is a structured (Flex) example; remove it and add a new one instead.` };
      }
      const newTexts = [...add, ...replace.map((r) => r?.text)].filter((text) => typeof text === 'string' && text.trim());
      for (const text of newTexts) {
        const detail = visitDetailIn(text, host);
        if (detail) {
          return {
            ok: false,
            error: `That example has ${detail}. Examples shape every future patient's note, so write it with bracketed generic terms (e.g. [Patient], [Date], [Body Part], [#]/10) and try again.`,
          };
        }
      }

      const edits = [];
      const replaced = new Map(replace.filter((r) => typeof r.text === 'string' && r.text.trim()).map((r) => [r.index, r.text]));
      const removed = new Set(remove);
      const after = [];
      before.forEach((example, index) => {
        const text = exampleText(example);
        if (removed.has(index)) {
          edits.push({ label: `Example ${index + 1} (removed)`, before: text, after: '' });
        } else if (replaced.has(index) && !sameText(replaced.get(index), text)) {
          edits.push({ label: `Example ${index + 1}`, before: text, after: replaced.get(index) });
          after.push(replaced.get(index));
        } else {
          after.push(example);
        }
      });
      add.filter((text) => typeof text === 'string' && text.trim()).forEach((text, i) => {
        edits.push({ label: `New example ${i + 1}`, before: '', after: text });
        after.push(text);
      });
      if (edits.length === 0) return { ok: false, noChange: true, error: 'No change to the examples.' };

      const allowed = await ctx.requestPermission({
        title: `Edit ${key} examples`,
        description: 'Applies to future notes on this template.',
        edits,
      });
      if (!allowed) return { ok: false, denied: true, error: 'The clinician did not allow this.' };
      const persisted = await persistInstructionUpdates(host.apiClient, loaded.configId, {}, { [key]: after });
      host.applyLocalExamples?.(persisted);
      return {
        ok: true,
        count: (persisted[key] || after).length,
        action: {
          status: 'done',
          summary: `Edited ${key} examples`,
          detail: edits.map(({ label, before: was, after: now }) => changeLine(label, was, now)).join('\n'),
          undo: { kind: 'examples', examples: { [key]: before } },
        },
      };
    },
  };

  const undoChange = {
    name: 'undo_change',
    permissionKey: 'undo_change@1',
    permissionLabel: 'Undo a change',
    description:
      "Put back exactly what was there before one of your earlier changes in this chat (note sections, template instructions, examples, a reply type or Additional Visit Information), by the action_id that change returned. Pass only to undo part of it: every section key or title, Flex subsection / group name, or field name to put back, all in one call (e.g. only: ['Plan', 'Treatment']); everything else stays as it is now. Asks the clinician first, showing the change back.",
    parameters: {
      type: 'object',
      properties: {
        action_id: { type: 'string', description: 'The action_id of the change to undo' },
        only: { type: 'array', items: { type: 'string' }, description: 'Undo just these sections, subsections or fields (optional)' },
      },
      required: ['action_id'],
    },
    handler: async ({ action_id: actionId, only }, ctx) => {
      const wanted = matcherFor(only);
      const action = ctx.findAction?.(actionId);
      if (!action) return { ok: false, error: `No change ${actionId} in this chat` };
      const { undo } = action;
      if (!undo) return { ok: false, error: `${action.summary} can't be undone from chat.` };
      const host = getHost();

      if (undo.kind === 'note') {
        if (!canWrite) return { ok: false, error: 'You can’t edit this note.' };
        const opened = await openNote(host);
        if (opened.error) return { ok: false, error: opened.error };
        const { session } = opened;
        const mismatch = await host.checkPatientMatch(session);
        if (mismatch) return { ok: false, error: `The EMR shows ${mismatch.emrPatientName}, not this visit's patient. Nothing was changed.` };
        const current = new Map(opened.sections.map((section) => [section.Key, section]));
        const restore = undo.sections
          .filter((snap) => current.has(snap.key))
          .map((snap) => {
            if (!wanted || wanted(snap.key, snap.title)) return snap;
            if (snap.type !== 'Flex') return null;
            // Part of any Flex representation: only the named path branches / fields
            const available = snapshotFlexEntries(snap, current.get(snap.key));
            const keep = available.filter(({ path }) =>
              wanted(...path.map(String), flexPathLabel(path)),
            );
            return keep.length ? {
              ...snap,
              answer: flexAnswerFromEntries(keep),
              paths: keep.map(({ path }) => path),
            } : null;
          })
          .map((snap) => {
            if (!snap || snap.type !== 'Flex') return snap;
            const now = current.get(snap.key);
            const desired = snapshotFlexEntries(snap, now);
            return {
              ...snap,
              answer: flexAnswerFromEntries(
                desired,
                flexFieldEntries(now.Structure, now.liveCurrentAnswer),
              ),
            };
          })
          .filter(Boolean);
        if (restore.length === 0) {
          return { ok: false, error: wanted ? `Nothing in that change matches ${only.join(', ')}.` : 'Those sections are not on the open note.' };
        }
        const edits = restore.flatMap((snap) => {
          const now = current.get(snap.key);
          if (snap.type === 'Flex') {
            const currentFields = new Map(flexFieldEntries(now.Structure, now.liveCurrentAnswer).map((field) => [flexPathKey(field.path), field.value]));
            return snapshotFlexEntries(snap, now)
              .filter(({ path, value }) => !sameFieldValue(currentFields.get(flexPathKey(path)), value))
              .map(({ path, value }) => ({
                label: `${snap.title} › ${flexPathLabel(path)}`,
                before: currentFields.get(flexPathKey(path)) ?? '',
                after: value,
              }));
          }
          return sameText(sectionText(now), snap.answer) ? [] : [{ label: snap.title, before: String(sectionText(now)), after: snap.answer }];
        });
        if (edits.length === 0) return { ok: false, noChange: true, error: 'The note already matches what was there before.' };
        const allowed = await ctx.requestPermission({
          title: `Undo: ${action.summary}`,
          destination: destinationOf(session),
          description: 'Puts back what was there before.',
          edits,
        });
        if (!allowed) return { ok: false, denied: true, error: 'The clinician did not allow this.' };
        if (session.isDisposed?.()) return { ok: false, error: 'The EMR page changed. Nothing was written.' };
        const redo = restore.map((snap) => noteSnapshot(
          current.get(snap.key),
          snap.type === 'Flex' ? snapshotFlexEntries(snap, current.get(snap.key)) : null,
        ));
        host.pushEmrUndo(session, restore.map((snap, index) => {
          const now = current.get(snap.key);
          return {
            Key: now.Key,
            Type: now.Type,
            ExistingNote: now.ExistingNote,
            Structure: now.Structure,
            Answer: snapshotAnswerForWrite(redo[index], now),
          };
        }));
        for (const snap of restore) {
          try {
            // eslint-disable-next-line no-await-in-loop
            await withTimeout(session.answerQuestionInEMR(snap.question, snap.answer, 'overwrite', snap.key), 'Writing');
          } catch (err) {
            return { ok: false, outcome: 'unknown', error: `${err.message}. Check ${snap.title} before trying again.` };
          }
          if (typeof snap.answer === 'string') host.apiClient.editConversation(host.conversationId, snap.key, snap.answer)?.catch?.(() => {});
        }
        return {
          ok: true,
          restored: restore.map((snap) => snap.title),
          ...(restore.some((snap) => snap.type === 'Flex') && {
            note: 'Flex fields that were empty before are left as they are now: the EMR ignores empty writes.',
          }),
          action: {
            status: 'done',
            summary: `Undid: ${action.summary}`,
            detail: edits.map(({ label, before, after }) => changeLine(label, before, after)).join('\n'),
            undo: { kind: 'note', sections: redo },
          },
        };
      }

      if (undo.kind === 'instructions' || undo.kind === 'reply_type' || undo.kind === 'examples') {
        if (!canEditTemplate) return { ok: false, error: 'You can’t edit this template.' };
        const loaded = await loadEditableTemplate(host);
        if (loaded.error) return { ok: false, error: loaded.error };
        const questions = loaded.details.questions || {};
        if (undo.kind === 'reply_type') {
          const now = loaded.details.replyType?.[undo.key] || 'overwrite';
          if (now === undo.replyType) return { ok: false, noChange: true, error: `${undo.key} is already ${now}.` };
          const allowed = await ctx.requestPermission({
            title: `Undo: ${action.summary}`,
            description: 'Puts the reply type back.',
            edits: [{ label: `${undo.key} reply type`, before: now, after: undo.replyType }],
          });
          if (!allowed) return { ok: false, denied: true, error: 'The clinician did not allow this.' };
          host.setTemplateReply(undo.key, undo.replyType);
          return {
            ok: true,
            action: {
              status: 'done',
              summary: `Undid: ${action.summary}`,
              detail: changeLine(`${undo.key} reply type`, now, undo.replyType),
              undo: { kind: 'reply_type', key: undo.key, replyType: now },
            },
          };
        }
        if (undo.kind === 'examples') {
          const keys = Object.keys(undo.examples).filter((key) => !wanted || wanted(key));
          const restoredExamples = Object.fromEntries(keys.map((key) => [key, undo.examples[key]]));
          const edits = keys
            .filter((key) => !sameText(JSON.stringify(loaded.details.samples?.[key] || []), JSON.stringify(undo.examples[key])))
            .map((key) => ({
              label: `${key} examples`,
              before: (loaded.details.samples?.[key] || []).map(exampleText).join('\n\n'),
              after: undo.examples[key].map(exampleText).join('\n\n'),
            }));
          if (edits.length === 0) return { ok: false, noChange: true, error: 'Those examples already match what was there before.' };
          const allowed = await ctx.requestPermission({ title: `Undo: ${action.summary}`, description: 'Puts the examples back.', edits });
          if (!allowed) return { ok: false, denied: true, error: 'The clinician did not allow this.' };
          const persisted = await persistInstructionUpdates(host.apiClient, loaded.configId, {}, restoredExamples);
          host.applyLocalExamples?.(persisted);
          return {
            ok: true,
            action: {
              status: 'done',
              summary: `Undid: ${action.summary}`,
              undo: { kind: 'examples', examples: Object.fromEntries(keys.map((key) => [key, loaded.details.samples?.[key] || []])) },
            },
          };
        }
        // Whole sections, or (with only) just the named subsections of a Flex instruction
        const targets = {};
        Object.keys(undo.instructions)
          .filter((key) => key in questions)
          .forEach((key) => {
            if (!wanted || wanted(key)) {
              targets[key] = undo.instructions[key];
              return;
            }
            const names = [
              ...Object.keys(parseFlexInstruction(questions[key])?.SubSections || {}),
              ...Object.keys(parseFlexInstruction(undo.instructions[key])?.SubSections || {}),
            ].filter((name, i, all) => all.indexOf(name) === i && wanted(name));
            if (names.length) targets[key] = restoreSubsections(questions[key], undo.instructions[key], names);
          });
        const keys = Object.keys(targets).filter((key) => !sameText(questions[key], targets[key]));
        // Examples the same change rewrote (the Instruction Assistant can do both)
        const samplesNow = loaded.details.samples || {};
        const examplesNow = (key) => (Array.isArray(samplesNow[key]) ? samplesNow[key] : []);
        const exampleKeys = Object.keys(undo.examples || {}).filter(
          (key) => (!wanted || wanted(key)) && !sameText(JSON.stringify(examplesNow(key)), JSON.stringify(undo.examples[key])),
        );
        if (keys.length === 0 && exampleKeys.length === 0) {
          return { ok: false, noChange: true, error: wanted ? `Nothing in that change matches ${only.join(', ')}, or it already matches.` : 'Those instructions already match what was there before.' };
        }
        const edits = [
          ...keys.map((key) => ({ label: key, before: instructionText(questions[key]), after: instructionText(targets[key]) })),
          ...exampleKeys.map((key) => ({
            label: `${key} examples`,
            before: examplesNow(key).map(exampleText).join('\n\n'),
            after: undo.examples[key].map(exampleText).join('\n\n'),
          })),
        ];
        const allowed = await ctx.requestPermission({
          title: `Undo: ${action.summary}`,
          description: exampleKeys.length ? 'Puts the instructions and examples back.' : 'Puts the instructions back.',
          edits,
        });
        if (!allowed) return { ok: false, denied: true, error: 'The clinician did not allow this.' };
        const restored = Object.fromEntries(keys.map((key) => [key, targets[key]]));
        const restoredExamples = Object.fromEntries(exampleKeys.map((key) => [key, undo.examples[key]]));
        const persisted = await persistInstructionUpdates(host.apiClient, loaded.configId, restored, restoredExamples);
        if (keys.length) host.applyLocalInstructions(restored);
        if (exampleKeys.length) host.applyLocalExamples?.(persisted);
        return {
          ok: true,
          restored: [...keys, ...exampleKeys.map((key) => `${key} examples`)],
          action: {
            status: 'done',
            summary: `Undid: ${action.summary}`,
            detail: edits.map(({ label, before, after }) => changeLine(label, before, after)).join('\n'),
            undo: {
              kind: 'instructions',
              instructions: Object.fromEntries(keys.map((key) => [key, questions[key]])),
              ...(exampleKeys.length && { examples: Object.fromEntries(exampleKeys.map((key) => [key, examplesNow(key)])) }),
            },
          },
        };
      }

      if (undo.kind === 'visit_notes') {
        const now = host.visitNotes || '';
        if (sameText(now, undo.visitNotes)) return { ok: false, noChange: true, error: 'Additional Visit Information already matches.' };
        const allowed = await ctx.requestPermission({
          title: `Undo: ${action.summary}`,
          description: 'Puts Additional Visit Information back.',
          before: now,
          after: undo.visitNotes,
        });
        if (!allowed) return { ok: false, denied: true, error: 'The clinician did not allow this.' };
        host.setVisitNotes(undo.visitNotes);
        return {
          ok: true,
          action: { status: 'done', summary: `Undid: ${action.summary}`, undo: { kind: 'visit_notes', visitNotes: now } },
        };
      }
      return { ok: false, error: 'That change can’t be undone from chat.' };
    },
  };

  // Read-only: the full earlier values behind one of the chat's changes, so the
  // agent can say what was there or put back part of it (undo puts back all of it)
  const getChange = {
    name: 'get_change',
    description:
      "Look up one of your earlier changes in this chat by its action_id: what it changed and the full values it replaced (note section text or Flex fields, instructions, examples, a reply type or Additional Visit Information). Read-only; nothing changes. Use it for \"what did it say before?\" or to restore only part of an old text with write_section; to put everything back, use undo_change.",
    parameters: {
      type: 'object',
      properties: { action_id: { type: 'string', description: 'The action_id of the earlier change' } },
      required: ['action_id'],
    },
    handler: async ({ action_id: actionId }, ctx) => {
      const action = ctx.findAction?.(actionId);
      if (!action) return { ok: false, error: `No change ${actionId} in this chat` };
      const { undo } = action;
      const base = { ok: true, summary: action.summary, ...(action.detail && { changed: action.detail }) };
      if (!undo) return { ...base, note: 'The earlier values of this change were not kept.' };
      if (undo.kind === 'note') {
        return {
          ...base,
          before: (undo.sections || []).map((snap) => ({
            key: snap.key,
            title: snap.title,
            type: snap.type,
            ...(snap.type === 'Flex' ? { fields: snap.answer } : { text: clip(snap.answer, CHANGE_VALUE_LIMIT) }),
          })),
        };
      }
      if (undo.kind === 'instructions') {
        return {
          ...base,
          before: {
            instructions: Object.fromEntries(
              Object.entries(undo.instructions || {}).map(([key, raw]) => [key, clip(instructionText(raw), CHANGE_VALUE_LIMIT)]),
            ),
            ...(undo.examples && { examples: undo.examples }),
          },
        };
      }
      if (undo.kind === 'examples') return { ...base, before: { examples: undo.examples } };
      if (undo.kind === 'reply_type') return { ...base, before: { section: undo.key, replyType: undo.replyType } };
      if (undo.kind === 'visit_notes') return { ...base, before: { visitInformation: clip(undo.visitNotes, CHANGE_VALUE_LIMIT) } };
      return base;
    },
  };

  return [
    getNoteSections,
    loadVisitTemplate,
    getTemplate,
    ...(canWrite ? [writeSection, comprehendNote, addVisitInformation] : []),
    ...(canEditTemplate ? [updateInstructions, setReplyType, editExamples, instructionAssistant] : []),
    ...(canWrite || canEditTemplate ? [undoChange, getChange] : []),
  ];
}
